import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { Language } from '../config/siteContent'
import InlineActionText from './InlineActionText'
import { ArrowIcon } from './icons'
import { Reveal, StaggerGroup } from './motion'

type ButtonLinkProps = {
  to: string
  children: ReactNode
  variant?: 'solid' | 'outline' | 'text'
  className?: string
}

export function ButtonLink({ to, children, variant = 'solid', className = '' }: ButtonLinkProps) {
  return (
    <Link className={`button button--${variant} ${className}`.trim()} to={to}>
      <span>{children}</span>
      <ArrowIcon className="button__icon" />
    </Link>
  )
}

type SectionHeadingProps = {
  eyebrow: string
  title: string
  text?: string
  align?: 'left' | 'center'
}

export function SectionHeading({ eyebrow, title, text, align = 'left' }: SectionHeadingProps) {
  return (
    <Reveal className={`section-heading section-heading--${align}`}>
      <p className="eyebrow">{eyebrow}</p>
      <h2>{title}</h2>
      {text && <p className="section-heading__text">{text}</p>}
    </Reveal>
  )
}

type PageIntroProps = {
  eyebrow: string
  title: string
  text: ReactNode
}

export function PageIntro({ eyebrow, title, text }: PageIntroProps) {
  return (
    <header className="page-intro shell">
      <p className="eyebrow">{eyebrow}</p>
      <div className="page-intro__grid">
        <h1>{title}</h1>
        <p>{text}</p>
      </div>
    </header>
  )
}

type CallToActionProps = {
  eyebrow: string
  title: string
  text: string
  button: string
  to: string
}

export function CallToAction({ eyebrow, title, text, button, to }: CallToActionProps) {
  return (
    <section className="cta-section section" aria-labelledby="cta-title">
      <div className="cta-section__glow" aria-hidden="true" />
      <div className="shell cta-section__inner">
        <Reveal>
          <p className="eyebrow">{eyebrow}</p>
          <h2 id="cta-title">{title}</h2>
          <p>{text}</p>
        </Reveal>
        <Reveal direction="right" delay={90}><ButtonLink to={to}>{button}</ButtonLink></Reveal>
      </div>
    </section>
  )
}

type NumberedCardProps = {
  number: string
  title: string
  text: string
  tags?: readonly string[]
}

export function NumberedCard({ number, title, text, tags }: NumberedCardProps) {
  return (
    <article className="numbered-card">
      <span className="numbered-card__number">{number}</span>
      <div className="numbered-card__body">
        <h3>{title}</h3>
        <p>{text}</p>
        {tags && (
          <ul className="tag-list" aria-label={title}>
            {tags.map((tag) => <li key={tag}>{tag}</li>)}
          </ul>
        )}
      </div>
    </article>
  )
}

type FaqItem = { question: string; answer: string }

export function FaqSection({ eyebrow, title, items, language }: { eyebrow: string; title: string; items: readonly FaqItem[]; language: Language }) {
  return (
    <section className="section faq-section" aria-labelledby="faq-title">
      <div className="shell faq-section__grid">
        <SectionHeading eyebrow={eyebrow} title={title} />
        <StaggerGroup className="faq-list">
          {items.map((item, index) => (
            <details key={item.question}>
              <summary><span>0{index + 1}</span>{item.question}</summary>
              <p><InlineActionText text={item.answer} language={language} /></p>
            </details>
          ))}
        </StaggerGroup>
      </div>
    </section>
  )
}
